import * as THREE from 'three';
import { SimplexNoise } from './Noise';

/**
 * Procedural textures (SPEC §5.2). Everything is generated in code — no external assets exist
 * anywhere in this project.
 *
 * These are built once at load into typed arrays and uploaded as DataTextures rather than drawn
 * through a canvas: no DOM dependency, so they can be built in a test, and no colour-space
 * surprises from the 2D context.
 */

const DETAIL_SIZE = 256;
const DETAIL_SEED = 0x0d_e7a11;

/**
 * The terrain detail map. Four channels of value noise at different scales, used to break up the
 * splat boundaries and to modulate albedo:
 *
 *   R — fine grain, perturbs the rock/scrub boundary so it is not a clean contour
 *   G — large-scale variation, the term that defeats visible tiling of the fine one
 *   B — mid-scale albedo mottling
 *   A — spare, reserved for the M7 decal mask
 */
export function createDetailTexture(): THREE.DataTexture {
  const noise = new SimplexNoise(DETAIL_SEED);
  const data = new Uint8Array(DETAIL_SIZE * DETAIL_SIZE * 4);

  for (let y = 0; y < DETAIL_SIZE; y += 1) {
    for (let x = 0; x < DETAIL_SIZE; x += 1) {
      const index = (y * DETAIL_SIZE + x) * 4;
      // Sampled on a torus so the texture tiles seamlessly: a visible seam every 18 m would be
      // far more obvious than any amount of noise.
      const u = (x / DETAIL_SIZE) * Math.PI * 2;
      const v = (y / DETAIL_SIZE) * Math.PI * 2;
      const tx = Math.cos(u) * 2.4;
      const ty = Math.sin(u) * 2.4;
      const tz = Math.cos(v) * 2.4;
      const tw = Math.sin(v) * 2.4;

      data[index] = toByte(noise.fbm(tx + tz * 0.31, ty + tw * 0.29, 4));
      data[index + 1] = toByte(noise.fbm(tx * 0.34 + 11.3, ty * 0.34 + 5.7, 3));
      data[index + 2] = toByte(noise.fbm(tx * 1.7 - 3.1, ty * 1.7 + 9.4, 3));
      data[index + 3] = 255;
    }
  }

  const texture = new THREE.DataTexture(data, DETAIL_SIZE, DETAIL_SIZE, THREE.RGBAFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Sea surface normal map: two octaves of value noise turned into a normal by Sobel filtering —
 * the one height-to-normal function reused for every surface (SPEC §5.2).
 */
export function createWaterNormalTexture(size = 256): THREE.DataTexture {
  const noise = new SimplexNoise(0x5ea_4a5e);
  const heights = new Float32Array(size * size);

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = (x / size) * Math.PI * 2;
      const v = (y / size) * Math.PI * 2;
      heights[y * size + x] =
        noise.fbm(Math.cos(u) * 3, Math.sin(u) * 3 + Math.cos(v) * 3, 4) * 0.6 +
        noise.fbm(Math.sin(v) * 6.3, Math.cos(u) * 6.3, 3) * 0.4;
    }
  }

  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const left = heights[y * size + ((x - 1 + size) % size)];
      const right = heights[y * size + ((x + 1) % size)];
      const down = heights[((y - 1 + size) % size) * size + x];
      const up = heights[((y + 1) % size) * size + x];

      const normal = new THREE.Vector3(left - right, 0.55, down - up).normalize();
      const index = (y * size + x) * 4;
      data[index] = Math.round((normal.x * 0.5 + 0.5) * 255);
      data[index + 1] = Math.round((normal.z * 0.5 + 0.5) * 255);
      data[index + 2] = Math.round((normal.y * 0.5 + 0.5) * 255);
      data[index + 3] = 255;
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

function toByte(signedNoise: number): number {
  const normalised = signedNoise * 0.5 + 0.5;
  const clamped = normalised < 0 ? 0 : normalised > 1 ? 1 : normalised;
  return Math.round(clamped * 255);
}
